import { Colors } from "../../config/DesignConstants";
import BaseEntity from "../../core/entity/BaseEntity";
import { shuffle } from "../../core/util/Random";
import { V } from "../../core/Vector";
import { TextAnchor, VectorText, VerticalAnchor } from "../text/VectorText";
import { UpgradeId } from "../util/UpgradeDefinitions";
import UpgradeManager from "../util/UpgradeManager";
import { UpgradeCard } from "./UpgradeCard";

// Number of upgrade options to present to the player
const MAX_UPGRADE_OPTIONS = 3;

export default class UpgradeMenu extends BaseEntity {
  private titleText: VectorText;
  private subtitleText: VectorText;
  private upgradeCards: UpgradeCard[] = [];

  private availableUpgrades: Array<{
    id: UpgradeId;
    name: string;
    description: string;
    rarity: "common" | "rare" | "epic";
  }> = [];

  constructor(private completedLevel: number) {
    super();

    // Create title text
    this.titleText = new VectorText({
      text: `LEVEL ${this.completedLevel} COMPLETE!`,
      scale: 4.0,
      strokeWidth: 3,
      color: Colors.CYAN,
      anchor: TextAnchor.CENTER,
      verticalAnchor: VerticalAnchor.CENTER,
      layer: "menu",
    });

    // Create subtitle
    this.subtitleText = new VectorText({
      text: "CHOOSE AN UPGRADE:",
      scale: 2.0,
      strokeWidth: 2,
      color: Colors.WHITE,
      anchor: TextAnchor.CENTER,
      verticalAnchor: VerticalAnchor.CENTER,
      layer: "menu",
    });
  }

  private handleUpgradeChoice = (upgradeId: string) => {
    this.dispatch("upgradeChosen", { upgrade: upgradeId });
    this.destroy();
  };

  onAdd() {
    // Initialize available upgrades
    if (!this.game) return;
    const upgradeManager = this.game.entities.getById(
      "upgradeManager",
    ) as UpgradeManager;

    if (!upgradeManager) {
      console.error("UpgradeManager not found!");
      return;
    }

    const availableUpgrades = upgradeManager.getAvailableUpgrades();

    this.availableUpgrades = shuffle(
      availableUpgrades.map(([id, data]) => ({
        id: id as UpgradeId,
        ...data,
      })) || [],
    ).slice(0, MAX_UPGRADE_OPTIONS);

    // Create upgrade cards
    for (const upgrade of this.availableUpgrades) {
      const card = new UpgradeCard({
        id: upgrade.id,
        name: upgrade.name,
        description: upgrade.description,
        rarity: upgrade.rarity,
        onClick: this.handleUpgradeChoice,
        layer: "menu",
      });
      this.upgradeCards.push(card);
      this.addChild(card);
    }

    // Add text elements
    this.addChild(this.titleText);
    this.addChild(this.subtitleText);

    // Position everything
    this.updatePositions();
  }

  private updatePositions(): void {
    if (!this.game) return;

    // Get screen dimensions
    const screenWidth = this.game.renderer.app.renderer.width;
    const screenHeight = this.game.renderer.app.renderer.height;
    const centerX = screenWidth / 2;
    const centerY = screenHeight / 2;

    // Position title and subtitle
    this.titleText.position = V(centerX, centerY - 200);
    this.subtitleText.position = V(centerX, centerY - 140);

    // Position upgrade cards horizontally
    const cardSpacing = 240; // Card width + spacing
    const totalWidth = (this.upgradeCards.length - 1) * cardSpacing;
    const startX = centerX - totalWidth / 2;

    this.upgradeCards.forEach((card, index) => {
      card.setPosition(V(startX + index * cardSpacing, centerY + 20));
    });
  }
}
